import { entities, enums, selectors } from "@fraction/shared";
import { type InfiniteData, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useWebsocket from "react-use-websocket";
import fraction from "src/api/fraction";
import { useAuthUser } from "src/auth/useAuth";
import config from "src/config";
import { useMutation, useQuery } from "src/lib";
import { v5 as uuidv5 } from "uuid";
import { useAccountViewPreference } from "./useAccountViewPreference";

const NAMESPACE = "f9b2c51f-3a9a-4b1d-9c0e-0d1a1b9a1a2a";

export function useChat({
  applicationId,
  type = enums.ChatType.CONVEYANCER_EMPLOYEE,
  enabled = true,
  initialData,
  onInitialLoad,
}: {
  applicationId?: string;
  type?: enums.ChatType;
  enabled?: boolean;
  initialData?: entities.Chat[];
  onInitialLoad?: (hasChats: boolean) => void;
}) {
  const [initialLoad, setInitialLoad] = useState(false);
  const { isAuthenticated, user } = useAuthUser();
  const { preference } = useAccountViewPreference();
  const queryClient = useQueryClient();

  // Use the shared WebSocket connection's readyState for connection indicator.
  // Chat messages now arrive via the global "notifications" topic (handled by notificationStore),
  // so we no longer need a per-chat-topic subscription here.
  const { readyState } = useWebsocket(config.websocketURL, { share: true }, enabled);

  const queryKey = ["appChat", applicationId, type, isAuthenticated];

  const q = useInfiniteQuery({
    enabled: !!applicationId && isAuthenticated && enabled,
    queryKey,
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      if (!applicationId) {
        return { data: [] as entities.Chat[], nextCursor: null };
      }

      const result = await fraction.getChat({ applicationId, type, cursor: pageParam });
      if (!initialLoad) {
        onInitialLoad?.(!!result.data?.length);
        setInitialLoad(true);
      }
      return result;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    select: (data) => {
      // Pages are in reverse chronological order (page 0 = newest, page N = oldest).
      // Reverse page order then flatten so the final array is oldest-to-newest.
      const allChats = [...data.pages].reverse().flatMap((page) => page.data);

      return getGroupedUserChat(
        allChats.map((x) => ({
          ...x,
          user: selectors.user.anonymizedUser(x.user, preference === "broker"),
        }))
      );
    },
  });

  const members = useQuery({
    enabled: !!applicationId && isAuthenticated && enabled,
    queryKey: ["appChatMembers", applicationId, type, isAuthenticated],
    queryFn: () => (applicationId ? fraction.getChatMembers({ applicationId, type }) : null),
  });

  const sendMessage = useMutation({
    mutationFn: ({ message, applicationId }: { message: string; applicationId: string }) => {
      const createdAt = new Date();
      const ts = createdAt.getTime();

      type ChatPage = { data: entities.Chat[]; nextCursor: string | null };
      queryClient.setQueryData<InfiniteData<ChatPage>>(queryKey, (oldData) => {
        if (!oldData?.pages?.length) return oldData;
        const lastPageIndex = oldData.pages.length - 1;
        const lastPage = oldData.pages[lastPageIndex];
        const newChat = new entities.Chat({
          user: user || undefined,
          userId: user?.id,
          id: uuidv5(`${message}-${createdAt.toISOString()}`, NAMESPACE),
          createdAt,
          message,
          applicationId,
          type,
          ts,
        });
        const newPages = [...oldData.pages];
        newPages[lastPageIndex] = { ...lastPage, data: [...(lastPage.data || []), newChat] };
        return { ...oldData, pages: newPages };
      });
      return fraction.sendChat({ message, applicationId, type, ts });
    },
    onError: (_e) => {
      if (applicationId) {
        return queryClient.invalidateQueries({ queryKey });
      }
    },
  });

  return {
    ...q,
    isLoading: q.isLoading || members.isLoading,
    chats: q.data,
    members: members.data?.filter((u) => u.id !== user?.id),
    sendMessage: sendMessage.mutateAsync,
    isPending: sendMessage.isPending,
    readyState,
    fetchNextPage: q.fetchNextPage,
    hasNextPage: q.hasNextPage,
    isFetchingNextPage: q.isFetchingNextPage,
  };
}

export function getGroupedUserChat(chats_: entities.Chat[] | undefined | null) {
  const chats = chats_ || [];
  const userChats = [];
  let currentUser: string | undefined;
  let currentUserChats: entities.Chat[] = [];

  for (let i = 0; i < (chats || []).length; i++) {
    const chat = chats[i];
    if (!chat.userId) {
      continue;
    }

    if (currentUser === chat.userId) {
      currentUserChats.push(chat);
    } else {
      if (currentUserChats?.length) {
        userChats.push(currentUserChats);
      }
      currentUserChats = [chat];
      currentUser = chat.userId;
    }

    if (i === chats.length - 1) {
      userChats.push(currentUserChats);
    }
  }

  return userChats.filter((c) => c.length).map((x) => ({ user: x?.[0]?.user, chats: x }));
}
